(() => {
  if (window.StoneRootsCartDrawer) return;

  const root = window.Shopify?.routes?.root || '/';
  let lastActiveElement = null;
  const drawer = () => document.querySelector('[data-cart-drawer]');
  const panel = () => drawer()?.querySelector('.cart-drawer__panel');
  const cartError = () => drawer()?.dataset.cartError || 'The cart could not be updated. Please try again.';

  const notifyCartUpdated = (cartData) => {
    if (!cartData) return;
    document.dispatchEvent(new CustomEvent('stone-roots:cart-updated', {
      detail: { cart: cartData }
    }));
  };

  const updateHeaderCount = (count) => {
    document.querySelectorAll('.site-header__cart-link').forEach((link) => {
      const countTemplate = drawer()?.dataset.cartCountTemplate;
      link.setAttribute('aria-label', countTemplate ? countTemplate.replace('999', String(count)) : `Cart, ${count} items`);
      let badge = link.querySelector('.site-header__cart-count');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'site-header__cart-count';
          badge.setAttribute('aria-hidden', 'true');
          link.appendChild(badge);
        }
        badge.textContent = String(count);
      } else {
        badge?.remove();
      }
    });
  };

  const open = () => {
    const element = drawer();
    if (!element) return;
    lastActiveElement = document.activeElement;
    element.classList.add('is-open');
    element.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    window.requestAnimationFrame(() => element.querySelector('[data-cart-drawer-close]')?.focus());
  };

  const close = () => {
    const element = drawer();
    if (!element) return;
    element.classList.remove('is-open');
    element.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    if (lastActiveElement instanceof HTMLElement) lastActiveElement.focus();
  };

  const showError = (message) => {
    const status = drawer()?.querySelector('[data-cart-drawer-status]');
    if (status) status.textContent = message || cartError();
  };

  const refresh = async (cartData) => {
    const currentDrawer = drawer();
    if (!currentDrawer) return;
    const sectionId = currentDrawer.dataset.sectionId;
    const response = await fetch(`${root}?section_id=${encodeURIComponent(sectionId)}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error(cartError());
    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const nextDrawer = documentFragment.querySelector('[data-cart-drawer]');
    if (!nextDrawer) throw new Error(cartError());
    nextDrawer.classList.add('is-open');
    nextDrawer.setAttribute('aria-hidden', 'false');
    currentDrawer.replaceWith(nextDrawer);
    if (cartData) updateHeaderCount(cartData.item_count);
    notifyCartUpdated(cartData);
    document.body.classList.add('cart-drawer-open');
    window.requestAnimationFrame(() => nextDrawer.querySelector('[data-cart-drawer-close]')?.focus());
  };

  const getCart = async () => {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(cartError());
    return response.json();
  };

  const openAndRefresh = () => {
    open();
    return getCart().then((cartData) => refresh(cartData)).catch((error) => showError(error.message));
  };

  const changeLine = async (key, quantity) => {
    const element = drawer();
    element?.classList.add('is-loading');
    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message);
      await refresh(data);
    } catch (error) {
      element?.classList.remove('is-loading');
      showError(error.message);
    }
  };

  const addProduct = async (form, submitter) => {
    const submit = submitter || form.querySelector('[name="add"]');
    if (submit?.disabled) return;
    const originalLabel = submit?.querySelector('[data-submit-label]')?.textContent;
    if (submit) submit.disabled = true;
    const label = submit?.querySelector('[data-submit-label]');
    const addingLabel = drawer()?.dataset.cartAdding || 'Adding…';
    if (label) label.textContent = addingLabel;

    try {
      const quantityControl = form.querySelector('[data-quantity-control]');
      const formData = new FormData(form);
      const selectedOption = form.querySelector('select[name="id"] option:checked');
      const maximum = Number(selectedOption?.dataset.quantityMax || quantityControl?.dataset.quantityMax) || null;
      const variantId = String(formData.get('id'));
      const requestedQuantity = Number(formData.get('quantity')) || 1;
      if (maximum) {
        const currentCart = await getCart();
        notifyCartUpdated(currentCart);
        const currentQuantity = currentCart.items.reduce((total, item) => {
          return String(item.variant_id) === variantId ? total + item.quantity : total;
        }, 0);
        const remainingQuantity = Math.max(maximum - currentQuantity, 0);
        if (requestedQuantity > remainingQuantity) {
          const message = remainingQuantity > 0
            ? (drawer()?.dataset.cartLimitMore || 'You can add only %count% more bottles to this order.').replace('%count%', String(remainingQuantity))
            : (drawer()?.dataset.cartLimitMax || 'Maximum %count% bottles per order.').replace('%count%', String(maximum));
          throw new Error(message);
        }
      }

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message);
      const cartData = await getCart();
      await refresh(cartData);
      open();
    } catch (error) {
      open();
      showError(error.message);
    } finally {
      const quantityControl = form.querySelector('[data-quantity-control]');
      if (submit) submit.disabled = Boolean(quantityControl?.hidden);
      if (label?.textContent.trim() === addingLabel && originalLabel) {
        label.textContent = originalLabel.trim();
      }
    }
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form.main-product__form');
    if (!form) return;
    event.preventDefault();
    addProduct(form, event.submitter);
  });

  document.addEventListener('stone-roots:open-cart', openAndRefresh);
  document.addEventListener('click', (event) => {
    const cartLink = event.target.closest('.site-header__cart-link');
    if (cartLink && !event.defaultPrevented && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      openAndRefresh();
      return;
    }
    if (event.target.closest('[data-cart-drawer-close]')) {
      close();
      return;
    }
    const item = event.target.closest('[data-cart-drawer-item]');
    if (!item) return;
    const key = item.dataset.lineKey;
    const currentQuantity = Number(item.querySelector('[data-cart-drawer-quantity]')?.textContent) || 1;
    const minimum = Number(item.dataset.quantityMin) || 1;
    const increment = Number(item.dataset.quantityIncrement) || 1;
    const maximum = Number(item.dataset.quantityMax) || null;
    if (event.target.closest('[data-cart-drawer-remove]')) {
      changeLine(key, 0);
    } else if (event.target.closest('[data-cart-drawer-minus]')) {
      changeLine(key, Math.max(minimum, currentQuantity - increment));
    } else if (event.target.closest('[data-cart-drawer-plus]')) {
      const nextQuantity = currentQuantity + increment;
      changeLine(key, maximum ? Math.min(nextQuantity, maximum) : nextQuantity);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer()?.classList.contains('is-open')) {
      close();
      return;
    }
    if (event.key !== 'Tab' || !drawer()?.classList.contains('is-open')) return;
    const focusable = Array.from(panel()?.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])') || []);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  window.StoneRootsCartDrawer = { open, close, refresh, openAndRefresh };
  if (window.Shopify?.actions?.openCart?.configure) {
    window.Shopify.actions.openCart.configure({ handler: open });
  }
})();
